/**
 * CPU-offload subprocess pool.
 *
 * A small set of long-lived child processes that run pure, CPU-bound tasks
 * ({@link ./tasks.ts}) off the host's event loop. Each worker handles ONE
 * request at a time so a hung task can be reclaimed precisely: on timeout the
 * host SIGKILLs that worker and respawns a replacement. (Bun's
 * `worker_threads.terminate()` and Effect's cooperative interruption cannot
 * interrupt non-yielding synchronous JS — only an OS signal to a separate
 * process can — which is why this is a process pool, not a thread pool.)
 *
 * Workers are acquired per-request and released the instant the call settles,
 * so a small pool (default ~half the vCPUs) serves many concurrent browser jobs
 * that each only need a worker for the brief slice when they format a snapshot.
 *
 * The supervisor mechanics (spawn, readiness handshake, idle reuse, waiter
 * queue, timeout → SIGKILL → respawn, drain) live in the generic
 * {@link ../worker-pool/worker-pool.js WorkerPool} — this module is now its
 * first {@link WorkerDriver}: a framed-stdio connection over {@link ./protocol.js}.
 * `CpuOffloadPool` is a thin facade preserving the original API and typed errors.
 *
 * @module cpu-offload/pool
 */

import { spawn } from 'node:child_process';
import { existsSync } from 'node:fs';
import { createRequire } from 'node:module';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import type { Writable } from 'node:stream';
import type { BrowserLogger } from '../logger.js';
import {
  type ChildHandle,
  type DriverHooks,
  type WorkerDriver,
  WorkerPool,
  WorkerPoolError,
  WorkerPoolOomError,
  type WorkerPoolLeaseGraceCounts,
  type WorkerPoolResourceStats,
  type WorkerPoolSaturationStats,
  type WorkerPoolRetirementCounts,
  WorkerPoolTimeoutError,
} from '../worker-pool/worker-pool.js';
import {
  encodeFrame,
  FrameDecoder,
  READY_ID,
  type CpuOffloadRequest,
  type CpuOffloadResponse,
} from './protocol.js';

/** A task ran longer than its timeout; the worker was SIGKILLed. */
export class CpuOffloadTimeoutError extends Error {
  readonly _tag = 'CpuOffloadTimeoutError';
  constructor(task: string, timeoutMs: number) {
    super(`cpu-offload task '${task}' exceeded ${timeoutMs}ms and was terminated`);
    this.name = 'CpuOffloadTimeoutError';
  }
}

/** The worker crashed, the task threw, or the pool could not spawn a worker. */
export class CpuOffloadWorkerError extends Error {
  readonly _tag = 'CpuOffloadWorkerError';
  constructor(message: string) {
    super(message);
    this.name = 'CpuOffloadWorkerError';
  }
}

/** A worker exceeded its RSS cap; the supervisor SIGKILLed it (the offending worker only). */
export class CpuOffloadOomError extends Error {
  readonly _tag = 'CpuOffloadOomError';
  constructor(task: string, message: string) {
    super(`cpu-offload task '${task}' was terminated: ${message}`);
    this.name = 'CpuOffloadOomError';
  }
}

/** Snapshot of pool occupancy + the kill/RSS counters, for host telemetry. */
export interface CpuOffloadPoolStats {
  /** Driver label (always `'cpu-offload'`) — the per-driver telemetry dimension. */
  driver: string;
  total: number;
  idle: number;
  busy: number;
  waiting: number;
  /** Lifetime kill counts by cause (cumulative). */
  kills: { timeout: number; oom: number; crash: number };
  /** Lifetime hard retirements with precise bounded reasons. */
  retirements: WorkerPoolRetirementCounts;
  /** Lifetime delayed-sweep lease grace outcomes. */
  leaseGrace: WorkerPoolLeaseGraceCounts;
  /** Highest child RSS the monitor observed (bytes; 0 if it never ran). */
  rssHighWaterBytes: number;
  /** Whether the RSS-cap circuit-breaker has latched (cap disabled after repeated immediate OOMs). */
  rssBreakerTripped: boolean;
  /** Queue wait / depth high-water / time starved — see {@link WorkerPoolSaturationStats}. */
  saturation: WorkerPoolSaturationStats;
  /** Per-driver CPU/PSS percentiles; present only while calibration sampling is on. */
  resourceStats?: WorkerPoolResourceStats;
}

export interface CpuOffloadPoolOptions {
  /** Max concurrent worker processes. Workers spawn lazily up to this. */
  size: number;
  /** Default per-call wall-clock budget before SIGKILL. */
  defaultTimeoutMs: number;
  /** Per-worker RSS cap (bytes). Unset ⇒ the RSS monitor never runs. */
  rssBytes?: number;
  /** RSS-monitor cadence in ms (defaults to the pool's 1 Hz). */
  rssCheckIntervalMs?: number;
  /** Arm the per-driver CPU/PSS calibration sampler at this cadence (ms). Unset ⇒ off. */
  measureResourcesMs?: number;
  /** Override the worker entry path (tests). Defaults to the resolved sibling. */
  workerPath?: string;
  /** Override the runtime used to spawn workers (defaults to the current bun/node). */
  execPath?: string;
  /** Injectable spawn (tests). Defaults to node:child_process spawn. */
  spawn?: typeof spawn;
  /** Injectable per-child RSS reader (tests). Defaults to the pool's /proc reader. */
  readRssBytes?: (pid: number) => Promise<number | undefined>;
  logger?: BrowserLogger;
}

/**
 * Resolve the worker entry, tolerating three layouts:
 * 1. source / `bun test` — the sibling `.ts` next to this module;
 * 2. browser-core's own dist — `dist/cpu-offload/worker-process.js`;
 * 3. a bundled host (e.g. apps/api `bun build` inlines browser-core into
 *    `dist/server.js`, so `import.meta.url` points at the host bundle, not at
 *    browser-core's dist) — resolve via the package itself.
 */
export function resolveCpuOffloadWorkerPath(): string {
  const candidates = [
    new URL('./worker-process.ts', import.meta.url),
    new URL('./worker-process.js', import.meta.url),
    new URL('./cpu-offload/worker-process.js', import.meta.url),
  ].map((u) => fileURLToPath(u));
  const found = candidates.find((p) => existsSync(p));
  if (found) return found;

  // Bundled-host fallback: locate browser-core by name and walk to its dist.
  try {
    const pkgJson = createRequire(import.meta.url).resolve('@chatsdet/browser-core/package.json');
    const viaPkg = join(dirname(pkgJson), 'dist', 'cpu-offload', 'worker-process.js');
    if (existsSync(viaPkg)) return viaPkg;
    candidates.push(viaPkg);
  } catch {
    // fall through to the throw
  }
  throw new CpuOffloadWorkerError(`worker entry not found; looked in: ${candidates.join(', ')}`);
}

/**
 * A framed-stdio connection to one ready worker: dispatches one request at a
 * time (the pool guarantees one in-flight unit per child) and correlates the
 * matching response by id. A stdin-write failure or a decoder error is fatal —
 * it retires the worker via {@link DriverHooks.onFatal}.
 */
class CpuOffloadConn {
  private current: {
    id: number;
    resolve: (v: unknown) => void;
    reject: (e: Error) => void;
  } | null = null;
  private nextId = 1;

  constructor(
    private readonly stdin: Writable | null,
    private readonly onFatal: (err: Error) => void
  ) {}

  /** Send a registered task and resolve with its result (rejects on task error). */
  send<R>(task: string, payload: unknown): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      const id = this.nextId++;
      this.current = { id, resolve: resolve as (v: unknown) => void, reject };
      if (!this.stdin) {
        this.fatal(new CpuOffloadWorkerError('worker has no stdin'), reject);
        return;
      }
      const req: CpuOffloadRequest = { id, task, payload };
      try {
        this.stdin.write(encodeFrame(req));
      } catch (err) {
        this.fatal(
          new CpuOffloadWorkerError(
            `failed to write to worker: ${err instanceof Error ? err.message : String(err)}`
          ),
          reject
        );
      }
    });
  }

  /** Route a response frame to the in-flight call (ignores stale post-kill stragglers). */
  deliver(resp: CpuOffloadResponse): void {
    const call = this.current;
    if (!call || call.id !== resp.id) return;
    this.current = null;
    if (resp.ok) call.resolve(resp.result);
    else call.reject(new CpuOffloadWorkerError(resp.error.message));
  }

  private fatal(err: CpuOffloadWorkerError, reject: (e: Error) => void): void {
    this.current = null;
    // onFatal retires the worker, which synchronously settles the outer run().
    // We still reject this send promise: the killed child will never respond, so
    // without it the inner promise dangles forever (the pool's run() then leaks
    // this closure). The pool's own settle has already fired, so its handler is a
    // no-op — this purely prevents the dangling inner promise.
    this.onFatal(err);
    reject(err);
  }
}

/** The cpu-offload driver: spawns the framed-stdio worker and readies a {@link CpuOffloadConn}. */
class CpuOffloadDriver implements WorkerDriver<CpuOffloadConn> {
  readonly name = 'cpu-offload';
  readonly entry: { execPath: string; modulePath: string };
  readonly limits: { timeoutMs: number; rssBytes?: number };

  constructor(execPath: string, workerPath: string, defaultTimeoutMs: number, rssBytes?: number) {
    this.entry = { execPath, modulePath: workerPath };
    this.limits = { timeoutMs: defaultTimeoutMs, ...(rssBytes ? { rssBytes } : {}) };
  }

  ready(child: ChildHandle, hooks: DriverHooks): Promise<CpuOffloadConn> {
    return new Promise<CpuOffloadConn>((resolve, reject) => {
      if (!child.stdout) {
        reject(new CpuOffloadWorkerError('worker has no stdout'));
        return;
      }
      const decoder = new FrameDecoder();
      let conn: CpuOffloadConn | null = null;
      child.stdout.on('data', (chunk: Buffer) => {
        let frames: unknown[];
        try {
          frames = decoder.push(chunk);
        } catch (err) {
          // Malformed/oversized frame from our own child — treat as a crash.
          // Pre-ready, reject the spawn now; post-ready, retire the worker.
          const wrapped = new CpuOffloadWorkerError(
            `worker protocol error: ${err instanceof Error ? err.message : String(err)}`
          );
          if (conn) hooks.onFatal(wrapped);
          else reject(wrapped);
          return;
        }
        for (const msg of frames) {
          const resp = msg as CpuOffloadResponse;
          if (resp.id === READY_ID) {
            conn = new CpuOffloadConn(child.stdin, hooks.onFatal);
            resolve(conn);
          } else {
            conn?.deliver(resp);
          }
        }
      });
    });
  }
}

export class CpuOffloadPool {
  private readonly pool: WorkerPool<CpuOffloadConn>;
  private readonly defaultTimeoutMs: number;

  constructor(options: CpuOffloadPoolOptions) {
    this.defaultTimeoutMs = options.defaultTimeoutMs;
    const driver = new CpuOffloadDriver(
      options.execPath ?? process.execPath,
      options.workerPath ?? resolveCpuOffloadWorkerPath(),
      options.defaultTimeoutMs,
      options.rssBytes
    );
    this.pool = new WorkerPool<CpuOffloadConn>({
      size: options.size,
      driver,
      spawn: options.spawn,
      ...(options.readRssBytes ? { readRssBytes: options.readRssBytes } : {}),
      ...(options.rssCheckIntervalMs ? { rssCheckIntervalMs: options.rssCheckIntervalMs } : {}),
      ...(options.measureResourcesMs ? { measureResourcesMs: options.measureResourcesMs } : {}),
      logger: options.logger,
    });
  }

  /** Run a registered task. Rejects with a typed error on timeout/crash. */
  async run<R = unknown>(
    task: string,
    payload: unknown,
    runOpts?: { timeoutMs?: number }
  ): Promise<R> {
    const timeoutMs = runOpts?.timeoutMs ?? this.defaultTimeoutMs;
    try {
      return await this.pool.run<R>((conn) => conn.send<R>(task, payload), {
        timeoutMs,
        label: task,
      });
    } catch (err) {
      if (err instanceof WorkerPoolTimeoutError) {
        throw new CpuOffloadTimeoutError(task, timeoutMs);
      }
      if (err instanceof WorkerPoolOomError) throw new CpuOffloadOomError(task, err.message);
      if (err instanceof CpuOffloadWorkerError) throw err; // already typed (protocol/fatal)
      if (err instanceof WorkerPoolError) throw new CpuOffloadWorkerError(err.message);
      throw new CpuOffloadWorkerError(err instanceof Error ? err.message : String(err));
    }
  }

  stats(): CpuOffloadPoolStats {
    const s = this.pool.stats();
    return {
      // The driver always names itself ('cpu-offload'); the `??` only narrows the
      // generic pool's optional `driver` to the required field here.
      driver: s.driver ?? 'cpu-offload',
      total: s.size,
      idle: s.idle,
      busy: s.busy,
      waiting: s.queued,
      kills: s.kills,
      retirements: s.retirements,
      leaseGrace: s.leaseGrace,
      rssHighWaterBytes: s.rssHighWaterBytes,
      rssBreakerTripped: s.rssBreakerTripped,
      saturation: s.saturation,
      ...(s.resourceStats ? { resourceStats: s.resourceStats } : {}),
    };
  }

  /** Toggle the calibration sampler live (see {@link WorkerPool.setMeasureResourcesMs}). */
  setMeasureResourcesMs(ms: number): void {
    this.pool.setMeasureResourcesMs(ms);
  }

  async shutdown(): Promise<void> {
    await this.pool.drain();
  }
}
